import ReactPaginate from "react-paginate";
import { Eye, ShieldBan } from "lucide-react";

export default function LeadsTable({
    records, // displayed page
    filteredCount, // length of filtered dataset (before pagination)
    absoluteTotal, // full dataset size
    sortField,
    sortOrder,
    onSort,
    formatDate,
    onOpenDetails,
    onOpenUpdate,
    onOpenDelete,
    onOpenLinkedContext,
    onAddToBlacklist,
    onAddToQueue,
    onCreateLead,
    expandDisabled,
    leadDisabled,
    blacklistBusy,
    pageCount,
    currentPage,
    onPageChange,
    recordsPerPage,
    actionHidden,
}) {
    const start = filteredCount === 0 ? 0 : currentPage * recordsPerPage + 1;
    const end =
        filteredCount === 0
            ? 0
            : Math.min(filteredCount, (currentPage + 1) * recordsPerPage);

    const columns = [
        "domain",
        "company_name",
        "business_category",
        "country",
        "relevance",
        "authority",
        "dynamics_crm",
        "expanded_at",
        "created_at",
    ];

    return (
        <div className="w-full max-w-full">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                {/* Header */}
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Leads</div>
                        <div className="text-sm text-slate-600">
                            {filteredCount > 0
                                ? `Showing ${start}-${end} of ${filteredCount} record${filteredCount === 1 ? "" : "s"
                                }${absoluteTotal !== filteredCount ? ` (out of ${absoluteTotal})` : ""
                                }`
                                : `Showing 0 of ${absoluteTotal} records`}
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
                            Page {currentPage + 1} / {pageCount || 1}
                        </span>
                        {!actionHidden ? (
                            <span className="hidden sm:inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                                Click a row for details
                            </span>
                        ) : null}
                    </div>
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full table-fixed divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                {columns.map((col) => {
                                    const isSortable = col !== "dynamics_crm";
                                    const active = sortField === col;

                                    return (
                                        <th
                                            key={col}
                                            scope="col"
                                            {...(isSortable && { onClick: () => onSort(col) })}
                                            className={[
                                                "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700",
                                                isSortable ? "cursor-pointer select-none hover:bg-slate-100" : "cursor-default",
                                                active ? "bg-slate-100" : "",
                                            ].join(" ")}
                                        >
                                            <span className="inline-flex items-center gap-1">
                                                {col.replace(/_/g, " ")}
                                                {isSortable && active && (
                                                    <span className="text-slate-900">
                                                        {sortOrder === "asc" ? "↑" : "↓"}
                                                    </span>
                                                )}
                                            </span>
                                        </th>
                                    );
                                })}

                                {!actionHidden && (
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-200 bg-white">
                            {records.length > 0 ? (
                                records.map((rec) => (
                                    <tr
                                        key={rec.record_id}
                                        className="group cursor-pointer hover:bg-slate-50"
                                        onClick={() => onOpenDetails(rec)}
                                    >
                                        <td className="px-4 py-4 text-sm font-medium text-slate-900 max-w-[240px] truncate">
                                            {rec.domain && rec.domain !== "N/A" ? (
                                                <a
                                                    href={rec.domain.startsWith("http") ? rec.domain : `https://${rec.domain}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {rec.domain}
                                                </a>
                                            ) : (
                                                "N/A"
                                            )}
                                        </td>

                                        <td className="px-4 py-4 text-sm text-slate-900 max-w-[240px] truncate">
                                            {rec.company_name}
                                        </td>

                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                                            {rec.business_category}
                                        </td>

                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                                            {rec.country}
                                        </td>

                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                                            {rec.relevance}
                                        </td>

                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                                            {rec.authority}
                                        </td>

                                        <td className="px-4 py-4">
                                            <span
                                                className={[
                                                    "inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold",
                                                    rec.dynamics_owner
                                                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                                        : "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
                                                ].join(" ")}
                                            >
                                                {rec.dynamics_owner ? "✔️" : "❌"}
                                            </span>
                                        </td>

                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                                            {formatDate(rec.expanded_at)}
                                        </td>

                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-700">
                                            {formatDate(rec.created_at)}
                                        </td>

                                        {!actionHidden && (
                                            <td className="px-4 py-4 whitespace-nowrap text-left">
                                                <div className="flex justify-start gap-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenLinkedContext(rec);
                                                        }}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                        title="Linked Contacts"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onAddToBlacklist(rec.domain);
                                                        }}
                                                        disabled={blacklistBusy}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Add domain to blacklist"
                                                    >
                                                        <ShieldBan className="h-4 w-4" />
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenUpdate(rec);
                                                        }}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                        title="Update record"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-7.293 7.293A1 1 0 018.001 14H5a1 1 0 01-1-1v-3a1 1 0 01.293-.707l7.293-7.293zM14 6L11 9l-2-2 3-3 2 2z" />
                                                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenDelete(rec);
                                                        }}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/60 transition"
                                                        title="Delete record"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M9 2a1 1 0 00-1 1v1H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-4V3a1 1 0 00-1-1H9zm1 2H9v1h2V4zm0 2H9v6h2V6z"
                                                                clipRule="evenodd"
                                                            />
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M10 2a1 1 0 011 1v1h4a1 1 0 010 2v10a2 2 0 01-2 2H5a1 1 0 01-1-1V6a1 1 0 010-2h4V3a1 1 0 011-1z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onAddToQueue(rec.record_id);
                                                        }}
                                                        disabled={expandDisabled}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                        title="Force enhance & expand"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onCreateLead(rec.record_id);
                                                        }}
                                                        disabled={leadDisabled}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Create CRM lead"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={actionHidden ? columns.length : columns.length + 1}
                                        className="px-6 py-10 text-center text-sm text-slate-500"
                                    >
                                        No leads found for the current filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile cards */}
                <div className="block md:hidden space-y-3">
                    {records.map((rec) => (
                        <div
                            key={rec.record_id}
                            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:bg-slate-50 cursor-pointer"
                            onClick={() => onOpenDetails(rec)}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm text-slate-700">
                                        <span className="font-semibold text-slate-900">Domain:</span>{" "}
                                        {rec.domain && rec.domain !== "N/A" ? (
                                            <a
                                                href={rec.domain.startsWith("http") ? rec.domain : `https://${rec.domain}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="break-all text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {rec.domain}
                                            </a>
                                        ) : (
                                            "N/A"
                                        )}
                                    </p>
                                    <p className="mt-1 text-sm text-slate-700">
                                        <span className="font-semibold text-slate-900">Company:</span>{" "}
                                        {rec.company_name}
                                    </p>
                                </div>

                                <span
                                    className={[
                                        "shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
                                        rec.dynamics_owner
                                            ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                            : "bg-rose-50 text-rose-700 ring-rose-200",
                                    ].join(" ")}
                                >
                                    {rec.dynamics_owner ? "Recorded" : "Not recorded"}
                                </span>
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-700">
                                <div><span className="font-semibold text-slate-900">Category:</span> {rec.business_category}</div>
                                <div><span className="font-semibold text-slate-900">Country:</span> {rec.country}</div>
                                <div><span className="font-semibold text-slate-900">Relevance:</span> {rec.relevance}%</div>
                                <div><span className="font-semibold text-slate-900">Authority:</span> {rec.authority}</div>
                                <div className="col-span-2"><span className="font-semibold text-slate-900">Expanded:</span> {formatDate(rec.expanded_at)}</div>
                                <div className="col-span-2"><span className="font-semibold text-slate-900">Created:</span> {formatDate(rec.created_at)}</div>
                            </div>

                            {!actionHidden && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenLinkedContext(rec);
                                        }}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                        title="Linked Contacts"
                                    >
                                        <Eye className="h-4 w-4" />
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAddToBlacklist(rec.domain);
                                        }}
                                        disabled={blacklistBusy}
                                        className="inline-flex items-center justify-center rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 shadow-sm hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Add domain to blacklist"
                                    >
                                        Blacklist
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenUpdate(rec);
                                        }}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                        title="Update record"
                                    >
                                        Update
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenDelete(rec);
                                        }}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-rose-400/60 transition"
                                        title="Delete record"
                                    >
                                        Delete
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAddToQueue(rec.record_id);
                                        }}
                                        disabled={expandDisabled}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Force enhance & expand"
                                    >
                                        Enhance & Expand
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCreateLead(rec.record_id);
                                        }}
                                        disabled={leadDisabled}
                                        className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Create CRM lead"
                                    >
                                        Create lead
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Pagination */}
                <div className="mt-6">
                    <ReactPaginate
                        previousLabel={
                            <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
                                ← Previous
                            </span>
                        }
                        nextLabel={
                            <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
                                Next →
                            </span>
                        }
                        pageCount={pageCount}
                        forcePage={currentPage}
                        onPageChange={({ selected }) => onPageChange(selected)}
                        containerClassName="flex flex-wrap items-center justify-center gap-2"
                        pageClassName="block"
                        pageLinkClassName="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition"
                        activeLinkClassName="!bg-slate-900 !text-white !border-slate-900"
                        disabledClassName="opacity-50 cursor-not-allowed"
                        breakClassName="px-2 py-2 text-slate-400"
                        renderOnZeroPageCount={null}
                    />
                </div>
            </div>
        </div>
    );
}
